import {
    inject,
    Injectable
} from "@angular/core";

import {
    Validators
} from "@angular/forms";

import {
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
    tilbyErrors
} from "@tilby/tilby-ui-lib/components/tilby-magic-form";

import { ConfigurationManagerService } from "src/app/core";
import { TranslateService } from "@ngx-translate/core";
import { color_items } from "../utils/item.constants";

import {
    ItemDataCatalogService,
    ItemDataOperationsService
} from "src/app/features/items";


@Injectable()
export class ItemsFormService {
    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly itemDataOperationService = inject(ItemDataOperationsService);
    private readonly configurationManager = inject(ConfigurationManagerService);
    private readonly translate = inject(TranslateService);
    
    createBulkEditForm(): CustomFormGroup<any> {
        const categories = this.itemDataCatalogService.getExtendedCategories() || [];
        const departments = this.itemDataCatalogService.getExtendedDepartments() || [];

        let priceDepartmentFormControls = {};
        const departmentsChoices = departments.map(dep => ({ value: dep.id ?? 0, 
            key: dep.name ? `${dep.name} ${this.itemDataOperationService.getDepartmentVat(dep.vat)}` : '' }));
        const requiredDepartmentsChoices = departmentsChoices.filter(d => d.value !== 0);
        const showPriceArray=Array.from({length:10},(_,i)=>this.configurationManager.getPreference(`price_list_${i+1}_hide`));
        const priceNameArray=Array.from({length:10},(_,i)=>this.configurationManager.getPreference(`price_list_${i+1}_name`) || 
            `${this.translate.instant('ITEMS.DETAILS.PRICE_LIST_START')} ${i+1}`);
        priceDepartmentFormControls = showPriceArray.reduce((state,hide,i)=>({
                ...state,
                ...(!hide && {
                    [`price${i+1}`]: new CustomFormControl(
                        null,
                        { validators: i ? Validators.min(0) : [Validators.required, Validators.min(0)] },
                        { ...new CustomFormControlProps(),
                            label: this.translate.instant('ITEMS.DETAILS.PRICE',{value:priceNameArray[i]}),
                            inputType:"number"
                        }),
                    [`department${i?i+1:''}_id`]: new CustomFormControl(
                        null,
                        { validators: Validators.required },
                        {...new CustomFormControlProps(),
                            label:`${this.translate.instant('ITEMS.DETAILS.DEPARTMENT')} ${priceNameArray[i]}`,
                            inputType:"select", 
                            errors: [...tilbyErrors],
                            inputChoices: i ? departmentsChoices : requiredDepartmentsChoices
                        })
                }),
            }),{}
        );

        const colorList = [...color_items];
        colorList.unshift({name: 'Nessuno', value: null});

        return new CustomFormGroup<any>({
            favorite: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.FAVORITE'
                }
            ),
            on_sale: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.FOR_SALE'
                }
            ),
            not_discountable: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.NOT_DISCOUNTABLE'
                }
            ),
            request_weighing: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-3',
                    label: 'ITEMS.DETAILS.REQUEST_WEIGHING'
                }
            ),
            category_id: new CustomFormControl(
                null,
                { validators: Validators.required },
                {
                    ...new CustomFormControlProps(),
                    inputType: 'select',
                    inputChoices: categories.map(cat => ({ value: cat.id ?? 0, key: cat.name ?? '' })),
                    label: 'ITEMS.DETAILS.CATEGORY'
                }
            ),
            color: new CustomFormControl(
                null,
                { validators: Validators.required },
                {
                    ...new CustomFormControlProps(),
                    inputType: 'select',
                    inputChoices: colorList?.map(({ value, name }) => ({ value: value || '0', key: name })) || [],
                    label: 'ITEMS.DETAILS.COLOR'
                }
            ),

            ...priceDepartmentFormControls,

            is_frozen: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.IS_FROZEN'
                }
            ),
            checkout_mark: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.CHECKOUT_MARK'
                }
            ),
            stock_type: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.ENABLE_SIMPLE_STOCK'
                }
            ),
            auto_unload: new CustomFormControl(
                false,
                {},
                {
                    ...new CustomFormControlProps(),
                    inputType: 'slideToggle',
                    class: 'tw-pb-2',
                    label: 'ITEMS.DETAILS.AUTO_UNLOAD'
                }
            )
        });
    }
}
